import { createContext, useContext, useCallback, useEffect, useMemo, useState } from "react";

const KEY = "meetiq_locale";

export const LOCALES = [
  { code: "en", label: "English", native: "English", flag: "EN" },
  { code: "hi", label: "Hindi", native: "हिन्दी", flag: "HI" },
  { code: "ta", label: "Tamil", native: "தமிழ்", flag: "TA" },
  { code: "te", label: "Telugu", native: "తెలుగు", flag: "TE" },
  { code: "es", label: "Spanish", native: "Español", flag: "ES" },
  { code: "fr", label: "French", native: "Français", flag: "FR" },
  { code: "ar", label: "Arabic", native: "العربية", flag: "AR" },
];

// Shell / navigation / common chrome strings. Product content stays in English.
const DICT = {
  en: {
    "nav.dashboard": "Dashboard", "nav.meetings": "Meetings", "nav.tasks": "Tasks",
    "nav.decisions": "Decisions", "nav.insights": "Insights", "nav.billing": "Billing",
    "nav.settings": "Settings", "nav.admin": "Administration", "nav.users": "Workspaces & users",
    "nav.platformBilling": "Platform billing", "nav.workspace": "Workspace", "nav.execution": "Execution",
    "action.search": "Search", "action.signOut": "Sign out", "action.theme": "Theme",
    "action.language": "Language", "action.commands": "Commands",
    "shell.tagline": "AI Meeting Intelligence", "shell.searchHint": "Search meetings, tasks, decisions…",
  },
  hi: {
    "nav.dashboard": "डैशबोर्ड", "nav.meetings": "मीटिंग्स", "nav.tasks": "कार्य",
    "nav.decisions": "निर्णय", "nav.insights": "विश्लेषण", "nav.billing": "बिलिंग",
    "nav.settings": "सेटिंग्स", "nav.admin": "प्रशासन", "nav.users": "वर्कस्पेस और उपयोगकर्ता",
    "nav.platformBilling": "प्लेटफ़ॉर्म बिलिंग", "nav.workspace": "वर्कस्पेस", "nav.execution": "निष्पादन",
    "action.search": "खोजें", "action.signOut": "साइन आउट", "action.theme": "थीम",
    "action.language": "भाषा", "action.commands": "कमांड",
    "shell.tagline": "एआई मीटिंग इंटेलिजेंस", "shell.searchHint": "मीटिंग, कार्य, निर्णय खोजें…",
  },
  ta: {
    "nav.dashboard": "டாஷ்போர்டு", "nav.meetings": "கூட்டங்கள்", "nav.tasks": "பணிகள்",
    "nav.decisions": "முடிவுகள்", "nav.insights": "பகுப்பாய்வு", "nav.billing": "கட்டணம்",
    "nav.settings": "அமைப்புகள்", "nav.admin": "நிர்வாகம்", "nav.users": "பணியிடங்கள் & பயனர்கள்",
    "nav.platformBilling": "தள கட்டணம்", "nav.workspace": "பணியிடம்", "nav.execution": "செயலாக்கம்",
    "action.search": "தேடு", "action.signOut": "வெளியேறு", "action.theme": "தீம்",
    "action.language": "மொழி", "action.commands": "கட்டளைகள்",
    "shell.tagline": "ஏஐ கூட்ட நுண்ணறிவு", "shell.searchHint": "கூட்டங்கள், பணிகள், முடிவுகளைத் தேடு…",
  },
  te: {
    "nav.dashboard": "డాష్‌బోర్డ్", "nav.meetings": "సమావేశాలు", "nav.tasks": "పనులు",
    "nav.decisions": "నిర్ణయాలు", "nav.insights": "విశ్లేషణలు", "nav.billing": "బిల్లింగ్",
    "nav.settings": "సెట్టింగ్‌లు", "nav.admin": "నిర్వహణ", "nav.users": "వర్క్‌స్పేస్‌లు & వినియోగదారులు",
    "nav.platformBilling": "ప్లాట్‌ఫాం బిల్లింగ్", "nav.workspace": "వర్క్‌స్పేస్", "nav.execution": "అమలు",
    "action.search": "శోధించు", "action.signOut": "సైన్ అవుట్", "action.theme": "థీమ్",
    "action.language": "భాష", "action.commands": "ఆదేశాలు",
    "shell.tagline": "ఏఐ సమావేశ మేధ", "shell.searchHint": "సమావేశాలు, పనులు, నిర్ణయాలు శోధించండి…",
  },
  es: {
    "nav.dashboard": "Panel", "nav.meetings": "Reuniones", "nav.tasks": "Tareas",
    "nav.decisions": "Decisiones", "nav.insights": "Analítica", "nav.billing": "Facturación",
    "nav.settings": "Ajustes", "nav.admin": "Administración", "nav.users": "Espacios y usuarios",
    "nav.platformBilling": "Facturación de plataforma", "nav.workspace": "Espacio", "nav.execution": "Ejecución",
    "action.search": "Buscar", "action.signOut": "Cerrar sesión", "action.theme": "Tema",
    "action.language": "Idioma", "action.commands": "Comandos",
    "shell.tagline": "Inteligencia de reuniones con IA", "shell.searchHint": "Buscar reuniones, tareas, decisiones…",
  },
  fr: {
    "nav.dashboard": "Tableau de bord", "nav.meetings": "Réunions", "nav.tasks": "Tâches",
    "nav.decisions": "Décisions", "nav.insights": "Analyses", "nav.billing": "Facturation",
    "nav.settings": "Paramètres", "nav.admin": "Administration", "nav.users": "Espaces et utilisateurs",
    "nav.platformBilling": "Facturation plateforme", "nav.workspace": "Espace", "nav.execution": "Exécution",
    "action.search": "Rechercher", "action.signOut": "Se déconnecter", "action.theme": "Thème",
    "action.language": "Langue", "action.commands": "Commandes",
    "shell.tagline": "Intelligence de réunion par IA", "shell.searchHint": "Rechercher réunions, tâches, décisions…",
  },
  ar: {
    "nav.dashboard": "لوحة التحكم", "nav.meetings": "الاجتماعات", "nav.tasks": "المهام",
    "nav.decisions": "القرارات", "nav.insights": "التحليلات", "nav.billing": "الفواتير",
    "nav.settings": "الإعدادات", "nav.admin": "الإدارة", "nav.users": "المساحات والمستخدمون",
    "nav.platformBilling": "فواتير المنصة", "nav.workspace": "مساحة العمل", "nav.execution": "التنفيذ",
    "action.search": "بحث", "action.signOut": "تسجيل الخروج", "action.theme": "المظهر",
    "action.language": "اللغة", "action.commands": "الأوامر",
    "shell.tagline": "ذكاء الاجتماعات بالذكاء الاصطناعي", "shell.searchHint": "ابحث في الاجتماعات والمهام والقرارات…",
  },
};

const I18nContext = createContext({ locale: "en", setLocale: () => {}, t: (k) => k, dir: "ltr" });

export function I18nProvider({ children }) {
  const [locale, setLocaleState] = useState(() => localStorage.getItem(KEY) || "en");
  const dir = locale === "ar" ? "rtl" : "ltr";

  useEffect(() => {
    localStorage.setItem(KEY, locale);
    document.documentElement.setAttribute("lang", locale);
    document.documentElement.setAttribute("dir", dir);
  }, [locale, dir]);

  const setLocale = useCallback((l) => setLocaleState(l), []);
  const t = useCallback(
    (key) => (DICT[locale] && DICT[locale][key]) || DICT.en[key] || key,
    [locale]
  );

  const value = useMemo(() => ({ locale, setLocale, t, dir }), [locale, setLocale, t, dir]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export const useI18n = () => useContext(I18nContext);
